import { CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS } from '@app/src/components/CreateNewRequestModal/Common/SelectFilterStep'
import useQueryFilters, { useGetApiQueryFilters } from '@app/src/hooks/queryFilters'
import type { QueryParamType } from '@app/src/hooks/queryState'
import { AUTOMATION_QUESTION_KEYS } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import ColleagueOwnerFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/ColleagueOwnerFilterSelect'
import CountryFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/CountryFilterSelect'
import CustomCategoriesFilterGrid, {
  useCustomCategories,
} from '@app/src/pages/Configurations/ConfigurationsPages/Common/CustomCategoriesFilterGrid'
import FinalRiskRatingFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/FinalRiskRatingFilterSelect'
import IndustryFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/IndustryFilterSelect'
import ProviderCategoryFilterSelect from '@app/src/pages/Configurations/ConfigurationsPages/Common/ProviderCategoryFilterSelect'
import type { AnswerFlagFormData } from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreateAnswerFlagModal'
import { ANSWER_FLAG_FORM_KEYS } from '@app/src/pages/Configurations/ConfigurationsPages/AnswerFlag/CreateAnswerFlagModal'
import { mapToQueryParamType, normalizeFilterGroups } from '@app/src/pages/QuestionEditor/AnswerClassification/utils'
import type { FilterGroup } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ProviderStandardCategoryNames } from '@app/src/types/categories'
import { equalsCaseInsensitive } from '@app/src/utils/helpersTs'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Accordion, AccordionDetails, AccordionSummary, Grid, Typography } from '@mui/material'
import type { PropsWithChildren } from 'react'
import React, { useEffect, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import useDeepCompareEffect from 'use-deep-compare-effect'

const hasActiveFilter = (filterNames: string[], filters: FilterGroup[]) =>
  filters.some(f => filterNames.some(fn => equalsCaseInsensitive(fn, f.name)))

type FilterAccordionSectionProps = PropsWithChildren<{
  titleId: string
  filterNames: string[]
  filters: FilterGroup[]
}>

const FilterAccordionSection: React.FC<FilterAccordionSectionProps> = ({ titleId, filterNames, filters, children }) => {
  const { formatMessage } = useIntl()
  const [expanded, setExpanded] = useState(hasActiveFilter(filterNames, filters))
  const hasInitialized = useRef(expanded)

  useEffect(() => {
    if (!hasInitialized.current && hasActiveFilter(filterNames, filters)) {
      setExpanded(true)
      hasInitialized.current = true
    }
  }, [filterNames, filters])

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
      disableGutters
      elevation={0}
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
        <Typography variant="subtitle1">{formatMessage({ id: titleId })}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 0 }}>
        <Grid container spacing={2}>
          {children}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

const AnswerFlagSegment: React.FC = () => {
  const { categories } = useCustomCategories()
  const filters = useGetApiQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)
  const { setQueryFilters } = useQueryFilters(CATEGORY_FILTER_DRAWER_ALLOWED_FILTERS)
  const { setValue, getValues } = useFormContext<AnswerFlagFormData>()

  useEffect(() => {
    const initialFilterValues = getValues(ANSWER_FLAG_FORM_KEYS.PROVIDER_FILTERS)
    if (!initialFilterValues) return
    const normalized = normalizeFilterGroups(initialFilterValues ?? [])
    const mappedFilters = normalized.map(mapToQueryParamType).filter((f): f is QueryParamType => f !== undefined)

    setQueryFilters(mappedFilters)
    setValue(AUTOMATION_QUESTION_KEYS.CATEGORY_FILTERS, normalized)
  }, [])

  useDeepCompareEffect(() => {
    const normalized = normalizeFilterGroups(filters)
    setValue(ANSWER_FLAG_FORM_KEYS.PROVIDER_FILTERS, normalized)
  }, [filters])

  return (
    <>
      <FilterAccordionSection
        titleId="answerFlagRules.creation.segment.firmographics"
        filterNames={['country.id', 'industry.id']}
        filters={filters}
      >
        <Grid item xs={6}>
          <CountryFilterSelect />
        </Grid>
        <Grid item xs={6}>
          <IndustryFilterSelect />
        </Grid>
      </FilterAccordionSection>

      <FilterAccordionSection
        titleId="answerFlagRules.creation.segment.standardCategories"
        filterNames={[
          ProviderStandardCategoryNames.ActivityStatus,
          'providerOwners.userId',
          ProviderStandardCategoryNames.Priority,
          ProviderStandardCategoryNames.ApprovalStatus,
          ProviderStandardCategoryNames.FinalRiskRating,
        ]}
        filters={filters}
      >
        <Grid item xs={6}>
          <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.ActivityStatus} />
        </Grid>
        <Grid item xs={6}>
          <ColleagueOwnerFilterSelect />
        </Grid>
        <Grid item xs={6}>
          <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.Priority} />
        </Grid>
        <Grid item xs={6}>
          <ProviderCategoryFilterSelect categoryName={ProviderStandardCategoryNames.ApprovalStatus} />
        </Grid>
        <Grid item xs={6}>
          <FinalRiskRatingFilterSelect />
        </Grid>
      </FilterAccordionSection>

      {Boolean(categories?.length) && (
        <FilterAccordionSection
          titleId="answerFlagRules.creation.segment.customCategories"
          filterNames={['categoryOptions.id']}
          filters={filters}
        >
          <CustomCategoriesFilterGrid formKey={ANSWER_FLAG_FORM_KEYS.PROVIDER_FILTERS} categories={categories ?? []} />
        </FilterAccordionSection>
      )}
    </>
  )
}

export default AnswerFlagSegment
